import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react"
import {IBasicResponse} from "../../types/api"
import {ANALYTICS_API_TOKEN, ANALYTICS_API_URL} from "../../utils/api"
import {ADMIN_TIME_ZONE, applyAdminRequestHeaders} from "../../utils/apiHeaders"
import {StoreState} from "../store"
import {
    IDashboardBranchSlaOrdersParams,
    IDashboardBranchSlaOrdersResponse,
    IDashboardHybridStatisticsResponse,
    IDashboardSalesBreakdownsParams,
    IDashboardSalesBreakdownsResponse,
    IDashboardStatisticsParams,
    IDashboardStatisticsResponse
} from "./DashboardApiTypes"

const DASHBOARD_DATE_HAS_ZONE_REGEXP = /([zZ]|[+-]\d{2}:\d{2})$/

const normalizeDashboardDate = (value?: string) => {
    if (!value) return value
    return DASHBOARD_DATE_HAS_ZONE_REGEXP.test(value) ? value : `${value}Z`
}

const normalizeDashboardBody = (body: Omit<IDashboardStatisticsParams, "lang" | "branch_id_list">) => ({
    ...body,
    date_from: normalizeDashboardDate(body.date_from),
    date_to: normalizeDashboardDate(body.date_to)
})

const rawAnalyticsBaseQuery = fetchBaseQuery({
    baseUrl: ANALYTICS_API_URL,
    prepareHeaders: (headers, {getState}) => {
        try {
            const token = ANALYTICS_API_TOKEN || (getState() as StoreState).auth?.token
            if (token) {
                headers.set("Authorization", `Bearer ${token}`)
            }
        } catch (e) {
            console.error(e)
        }
        return applyAdminRequestHeaders(headers)
    }
})

const analyticsBaseQuery: typeof rawAnalyticsBaseQuery = async (args, api, extraOptions) => {
    if (!ANALYTICS_API_URL) {
        return {
            error: {
                status: "CUSTOM_ERROR",
                error: "REACT_APP_ANALYTICS_API_URL is not configured"
            }
        }
    }

    return rawAnalyticsBaseQuery(args, api, extraOptions)
}

export const dashboardApi = createApi({
    reducerPath: "dashboardApi",
    baseQuery: analyticsBaseQuery,
    tagTypes: ["dashboard"],
    endpoints: (build) => ({
        getDashboardStatistics: build.query<IBasicResponse<IDashboardStatisticsResponse>, IDashboardStatisticsParams>({
            query: ({lang, branch_id_list, ...body}) => ({
                url: "v1/dashboard/statistics",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                },
                body: {
                    ...normalizeDashboardBody(body),
                    ...(branch_id_list?.length ? {branch_id_list} : {})
                }
            }),
            providesTags: ["dashboard"]
        }),
        getDashboardHybridStatistics: build.query<IBasicResponse<IDashboardHybridStatisticsResponse>, IDashboardStatisticsParams>({
            query: ({lang, branch_id_list, ...body}) => ({
                url: "v1/dashboard/hybrid-statistics",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                },
                body: {
                    ...normalizeDashboardBody(body),
                    ...(branch_id_list?.length ? {branch_id_list} : {})
                }
            }),
            providesTags: ["dashboard"]
        }),
        getDashboardSalesBreakdowns: build.query<IBasicResponse<IDashboardSalesBreakdownsResponse>, IDashboardSalesBreakdownsParams>({
            query: ({lang, branch_id_list, ...body}) => ({
                url: "v1/dashboard/sales-breakdowns",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                },
                body: {
                    ...normalizeDashboardBody(body),
                    ...(branch_id_list?.length ? {branch_id_list} : {})
                }
            }),
            providesTags: ["dashboard"]
        }),
        getDashboardBranchSlaOrders: build.query<IBasicResponse<IDashboardBranchSlaOrdersResponse>, IDashboardBranchSlaOrdersParams>({
            query: ({lang, ...body}) => ({
                url: "v1/dashboard/branch-sla-orders",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                },
                body: {
                    ...body,
                    date_from: normalizeDashboardDate(body.date_from),
                    date_to: normalizeDashboardDate(body.date_to)
                }
            })
        })
    })
})

export const {
    useGetDashboardStatisticsQuery,
    useGetDashboardHybridStatisticsQuery,
    useGetDashboardSalesBreakdownsQuery,
    useGetDashboardBranchSlaOrdersQuery
} = dashboardApi
